import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Lock, ArrowLeft, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';

const RefundIcon = ({ kind }) => {
  if (kind === 'yes') return <CheckCircle2 className="h-4 w-4 text-emerald-400 shrink-0" />;
  if (kind === 'no') return <XCircle className="h-4 w-4 text-rose-400 shrink-0" />;
  return <AlertTriangle className="h-4 w-4 text-amber-400 shrink-0" />;
};

export default function RefundPolicyPage() {
  const lastUpdated = 'July 7, 2026';
  const contactEmail = 'arynnlittlejohn@icloud.com';

  const table = [
    { situation: 'Withdraw from unlocked vault', refund: 'yes', refundText: 'Yes, no fee', speed: '1–5 business days' },
    { situation: 'Withdraw from locked vault early', refund: 'no', refundText: 'No, except exceptions', speed: 'N/A' },
    { situation: 'Locked vault after unlock date', refund: 'yes', refundText: 'Yes', speed: '1–5 business days' },
    { situation: 'Deposit charged but no vault balance', refund: 'yes', refundText: 'Full refund', speed: 'Within 48h' },
    { situation: 'Cancel Premium', refund: 'yes', refundText: 'Access until end of period', speed: 'End of billing month' },
    { situation: 'Premium refund request', refund: 'warn', refundText: 'Via Apple only', speed: '48h Apple response' },
    { situation: 'Bug / unauthorized transaction', refund: 'yes', refundText: 'Full refund + fees', speed: 'Within 48h' },
    { situation: 'Account deletion', refund: 'yes', refundText: 'Unlocked funds return; locked funds return after unlock', speed: 'Up to 30 days' },
  ];

  return (
    <div className="min-h-screen bg-vault" data-testid="refund-policy-page">
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-3xl mx-auto px-4">
          <div className="flex items-center justify-between h-14">
            <Link to="/" className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-md bg-[#9333EA] flex items-center justify-center">
                <Lock className="h-4 w-4 text-white" />
              </div>
              <span className="font-heading text-lg font-bold text-white tracking-tight">FutureVault</span>
            </Link>
            <Link to="/">
              <Button variant="ghost" size="sm" className="text-[#A19DB0] hover:text-white hover:bg-white/5" data-testid="refund-back-btn">
                <ArrowLeft className="h-4 w-4 mr-2" /> Back
              </Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-12">
        <h1 className="font-heading text-4xl font-black text-white tracking-tight mb-2">Refund Policy</h1>
        <p className="text-[#A19DB0] text-sm">Last updated: {lastUpdated}</p>
        <p className="text-[#A19DB0] text-sm">FutureVault LLC — a California limited liability company</p>
        <p className="text-[#A19DB0] text-sm mb-10">
          Contact:{' '}
          <a href={`mailto:${contactEmail}`} className="text-[#9333EA] underline" data-testid="refund-contact-email">
            {contactEmail}
          </a>
        </p>

        <div className="space-y-8 text-[#D4D1E0] leading-relaxed">
          <section>
            <p>
              At FutureVault, we&apos;re committed to transparency and fair treatment of every user. This Refund
              Policy explains how withdrawals, cancellations, and refunds work.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">1. Vault Deposits — Your Money, Always Yours</h2>
            <p>
              FutureVault is not a bank. All funds you deposit into vaults are held by our payment partner
              Stripe, Inc. in accounts registered to you (not to FutureVault LLC). Your money is your money.
            </p>

            <h3 className="font-heading text-lg font-semibold text-white mt-6 mb-2">Unlocked Vaults</h3>
            <p>
              You may withdraw the full withdrawable balance from any UNLOCKED vault at any time, at no cost.
              Withdrawals arrive in 1–5 business days via Stripe.
            </p>

            <h3 className="font-heading text-lg font-semibold text-white mt-6 mb-2">Locked Vaults</h3>
            <p>
              Locked vaults are designed to prevent early withdrawal — this is the core product value. You
              cannot withdraw before the unlock date, except in the exceptions below. The unlock date is
              displayed and acknowledged before every deposit.
            </p>

            <h3 className="font-heading text-lg font-semibold text-white mt-6 mb-2">Exceptions (When We Unlock Early)</h3>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>Verified death of account holder (with documentation from executor/next-of-kin).</li>
              <li>Verified medical emergency (with doctor/hospital letter).</li>
              <li>Court order (subpoena, garnishment, etc.).</li>
              <li>Bug or unauthorized transaction on our end (rare — full refund + Stripe fees covered).</li>
              <li>Account deletion with locked balances (auto-return after unlock date).</li>
            </ul>
            <p className="mt-3">
              Email{' '}
              <a href={`mailto:${contactEmail}`} className="text-[#9333EA] underline">{contactEmail}</a>{' '}
              to request an exception (response within 48h).
            </p>

            <h3 className="font-heading text-lg font-semibold text-white mt-6 mb-2">When You Can Submit a Refund Request</h3>
            <ul className="list-disc list-inside space-y-2 pl-2" data-testid="refund-fridays-policy">
              <li><strong className="text-white">Refund requests are only accepted on Fridays</strong> (Eastern Time). This keeps FutureVault a real savings platform and gives our team dedicated review days.</li>
              <li>Each account is limited to <strong className="text-white">2 approved refunds per rolling 90-day period</strong>. Beyond that, refunds require documented review (medical, court order, etc.).</li>
              <li>Approved requests are processed within <strong className="text-white">1–2 business days</strong>. Funds land on your card in 5–10 business days after that.</li>
              <li><strong className="text-white">The 2% platform service fee is non-refundable</strong>. When we refund a deposit, you get back your principal amount — the small platform fee you paid on the original deposit stays with FutureVault to cover the cost of holding and safeguarding your money. Exception: if a refund is due to a FutureVault error or unauthorized transaction, the fee is refunded in full.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">2. Premium Subscription ($4.99/month)</h2>
            <p>FutureVault Premium is billed by Apple via StoreKit.</p>

            <h3 className="font-heading text-lg font-semibold text-white mt-6 mb-2">To Cancel</h3>
            <p>
              iPhone: <span className="text-white">Settings &rsaquo; tap your name &rsaquo; Subscriptions &rsaquo; FutureVault Premium &rsaquo; Cancel</span>.
              Cancellation takes effect at end of current billing period.
            </p>

            <h3 className="font-heading text-lg font-semibold text-white mt-6 mb-2">Refunds</h3>
            <p>
              All Premium refund requests must go to Apple at{' '}
              <a href="https://reportaproblem.apple.com" className="text-[#9333EA] underline" target="_blank" rel="noreferrer">
                reportaproblem.apple.com
              </a>. FutureVault cannot process subscription refunds directly. Apple&apos;s own refund policy
              applies.
            </p>

            <h3 className="font-heading text-lg font-semibold text-white mt-6 mb-2">If Premium Features Don&apos;t Work</h3>
            <p>
              Email{' '}
              <a href={`mailto:${contactEmail}`} className="text-[#9333EA] underline">{contactEmail}</a>. We
              investigate within 24h, manually grant access, and support your Apple refund request if
              unresolvable.
            </p>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">3. General</h2>
            <ul className="list-disc list-inside space-y-2 pl-2">
              <li>Standard support response: 24h (Mon–Fri Pacific).</li>
              <li>Urgent / money-blocking: 48h response, 7 days a week.</li>
              <li>
                Contact:{' '}
                <a href={`mailto:${contactEmail}`} className="text-[#9333EA] underline">{contactEmail}</a>{' '}
                |{' '}
                <a href="https://future-vault.com" className="text-[#9333EA] underline">future-vault.com</a>
              </li>
              <li>Governing law: California, Sacramento County.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading text-xl font-bold text-white mb-3">Quick Reference</h2>
            <div className="rounded-lg border border-white/10 overflow-hidden" data-testid="refund-quick-reference">
              <table className="w-full text-sm">
                <thead className="bg-white/[0.04] text-[#C8A6FF]">
                  <tr>
                    <th className="text-left px-4 py-3 font-semibold">Situation</th>
                    <th className="text-left px-4 py-3 font-semibold">Refund?</th>
                    <th className="text-left px-4 py-3 font-semibold hidden sm:table-cell">How Fast</th>
                  </tr>
                </thead>
                <tbody>
                  {table.map((row, i) => (
                    <tr key={i} className="border-t border-white/5">
                      <td className="px-4 py-3 text-white align-top">{row.situation}</td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex items-start gap-2">
                          <RefundIcon kind={row.refund} />
                          <span className="text-[#D4D1E0]">{row.refundText}</span>
                        </div>
                        <div className="text-xs text-[#8B8299] mt-1 sm:hidden">{row.speed}</div>
                      </td>
                      <td className="px-4 py-3 text-[#A19DB0] align-top hidden sm:table-cell">{row.speed}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          <section>
            <p>
              Questions? Reach us at{' '}
              <a href={`mailto:${contactEmail}`} className="text-[#9333EA] underline">{contactEmail}</a>.
            </p>
            <address className="not-italic mt-6 text-[#D7CCE6] leading-relaxed" data-testid="refund-contact-address">
              FutureVault LLC<br />
              212 Skyline Ridge Loop<br />
              Sacramento, CA 95834-2134<br />
              United States
            </address>
          </section>
        </div>

        <div className="mt-12 flex gap-4 text-sm text-[#A19DB0]">
          <Link to="/privacy" className="hover:text-white" data-testid="refund-to-privacy-link">Privacy Policy</Link>
          <span>·</span>
          <Link to="/terms" className="hover:text-white" data-testid="refund-to-terms-link">Terms of Service</Link>
          <span>·</span>
          <Link to="/support" className="hover:text-white" data-testid="refund-to-support-link">Support</Link>
        </div>
      </main>
    </div>
  );
}
